import { AxiosError } from 'axios';
import { tLeadsByFunnel } from 'src/pages/Dashboard/Dashboard.types';
import { AxiosService } from '../../../../axios/axios.service';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../../../lib/logging';
import {
  BillingError,
  tLeadsalesResponse,
} from '../../../../providers/SubscriptionProvider/Subscription.types';
import {
  t3DSecureConfirmationPayload,
  t3DSecureConfirmationResponse,
  tAddedSeats,
  tAddOnToPurchase,
  tAttachPaymentMethodResponse,
  tGetPaymentsResponse,
  tInvoicePreview,
  tInvoicePreviewRequest,
  tMakePaymentMethodDefaultPayload,
  tPaymentMethod,
  tPaymentMethodDefaultReport,
  tPaymentMethodDefaultRefusal,
  tPlanPauseResumePreview,
  tPlanPauseResumeResult,
  tPayPendingDebtsContract2Response,
  tPayPendingDebtsPayload,
  tPayPendingDebtsRequest,
  tPayPendingDebtsResponse,
  tProducts,
  tWorkspaceFeatureOverride,
  tProductToPurchase,
  tPurchaseAddOnPayload,
  tPurchaseProductOptions,
  tPurchaseProductPayload,
  tPurchaseProductResponse,
  tRenewalProjectionEnvelope,
  tRenewalProjectionRequest,
  tRenewalProjectionWireData,
  tRenewalProjectionWireFirstCharge,
  tRenewalProjectionWireLeadAgentLine,
  tRenewalProjectionWireLine,
  tRetentionFlowAbortPayload,
  tRetentionFlowFinalActionPayload,
  tRetentionFlowReasonSelectedPayload,
  tRetentionFlowResponse,
  tRetentionFlowStartPayload,
  tRetentionOfferAnswer,
  tSeatsResponse,
  tSubscriptionUpgradeRequest,
  tSubscriptionUpgradeResponse,
} from './SubscriptionManager.types';
import { STRIPE_TIMEOUT_MS } from './SubscriptionManager.constants';

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Validates and adapts the wire payload of `getRenewalProjection` into a typed
 * `tRenewalProjectionWireData`. EXPORTED so dev harnesses can push a fixture
 * through the same validator the service uses, instead of hand-shaping the
 * parsed type — a typed literal only proves it matches today's TS type; running
 * it proves it survives the runtime contract. Returns null when the minimum shape is missing
 * so the caller rejects instead of trusting an `as`-cast (the bff emits real
 * JSON `null` for absent dates, so a null guard is enough — no Go zero-value
 * `""` ambiguity here). Numeric fields must be finite numbers.
 */
export function adaptRenewalProjection(
  payload: unknown,
): tRenewalProjectionWireData | null {
  if (!payload || typeof payload !== 'object') return null;
  const wire = payload as Record<string, unknown>;

  if (!isString(wire.currency) || !isString(wire.frequency)) return null;
  if (!isString(wire.renewalDate)) return null;
  if (wire.renewalDateSource !== 'stripe' && wire.renewalDateSource !== 'local')
    return null;
  if (!isFiniteNumber(wire.projectedTotalCents)) return null;
  if (!Array.isArray(wire.lines)) return null;

  const lines: tRenewalProjectionWireLine[] = [];
  for (const raw of wire.lines) {
    if (!raw || typeof raw !== 'object') return null;
    const line = raw as Record<string, unknown>;
    if (
      !isString(line.name) ||
      !isString(line.type) ||
      !isFiniteNumber(line.amountCents) ||
      !isFiniteNumber(line.quantity)
    ) {
      return null;
    }
    lines.push({
      name: line.name,
      type: line.type,
      amountCents: line.amountCents,
      quantity: line.quantity,
    });
  }

  const rawLeadAgent = wire.leadAgentLine;
  if (!rawLeadAgent || typeof rawLeadAgent !== 'object') return null;
  const la = rawLeadAgent as Record<string, unknown>;
  if (!isString(la.tier) || !isFiniteNumber(la.amountCents)) return null;
  const leadAgentLine: tRenewalProjectionWireLeadAgentLine = {
    tier: la.tier,
    amountCents: la.amountCents,
  };

  const rawFirstCharge = wire.firstCharge;
  if (!rawFirstCharge || typeof rawFirstCharge !== 'object') return null;
  const fc = rawFirstCharge as Record<string, unknown>;
  if (!isFiniteNumber(fc.amountCents)) return null;
  // `kind` is the authoritative signal (RET-1290): reject an unknown value so
  // the modal fails closed instead of mis-rendering the breakdown.
  if (fc.kind !== 'full' && fc.kind !== 'bridge') return null;
  // `months` is optional on the wire (omitempty drops 0); coerce to a finite
  // number, defaulting to 0 when absent.
  const months = isFiniteNumber(fc.months) ? fc.months : 0;
  // `prorated` / `estimated` are LEGACY flags the wire will eventually drop. Key
  // the modal off `kind` only; treat them as optional so a future wire-shrink
  // (or a Go `omitempty` false) does not fail-close a valid projection. Default
  // `prorated` from `kind`, `estimated` to false (the amount is exact now).
  const prorated =
    typeof fc.prorated === 'boolean' ? fc.prorated : fc.kind === 'bridge';
  const estimated = typeof fc.estimated === 'boolean' ? fc.estimated : false;
  // `date` is a real ISO string or real JSON null.
  if (fc.date !== null && !isString(fc.date)) return null;
  const firstCharge: tRenewalProjectionWireFirstCharge = {
    amountCents: fc.amountCents,
    date: fc.date,
    kind: fc.kind,
    months,
    prorated,
    estimated,
  };

  return {
    currency: wire.currency,
    frequency: wire.frequency,
    renewalDate: wire.renewalDate,
    renewalDateSource: wire.renewalDateSource,
    lines,
    leadAgentLine,
    projectedTotalCents: wire.projectedTotalCents,
    firstCharge,
  };
}

function isPositiveInteger(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isInteger(value) && value > 0;
}

function isMinorUnitAmount(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isSafeInteger(value) && value >= 0;
}

function isIsoInstant(value: unknown): value is string {
  return isString(value) && value !== '' && !Number.isNaN(Date.parse(value));
}

function adaptPlanPauseTerms(payload: unknown) {
  if (!payload || typeof payload !== 'object') return null;
  const wire = payload as Record<string, unknown>;

  if (
    !isString(wire.currency) ||
    wire.currency === '' ||
    !isMinorUnitAmount(wire.monthly_amount) ||
    !Array.isArray(wire.month_options) ||
    !isString(wire.restore_plan_name) ||
    wire.restore_plan_name === '' ||
    !isPositiveInteger(wire.restore_seats) ||
    !isMinorUnitAmount(wire.restore_amount) ||
    typeof wire.discounts_restart_at_restore !== 'boolean'
  ) {
    return null;
  }

  const options = wire.month_options.map((raw) => {
    if (!raw || typeof raw !== 'object') return null;
    const option = raw as Record<string, unknown>;
    if (
      (option.months !== 1 && option.months !== 2 && option.months !== 3) ||
      !isIsoInstant(option.starts_at) ||
      !isIsoInstant(option.ends_at)
    ) {
      return null;
    }
    return {
      months: option.months,
      startsAt: option.starts_at,
      endsAt: option.ends_at,
    };
  });

  if (
    options.some((option) => option === null) ||
    options.length !== 3 ||
    new Set(options.map((option) => option?.months)).size !== 3
  ) {
    return null;
  }

  return {
    currency: wire.currency.toUpperCase(),
    monthlyAmount: wire.monthly_amount,
    monthOptions: options as Array<{
      months: 1 | 2 | 3;
      startsAt: string;
      endsAt: string;
    }>,
    restorePlanName: wire.restore_plan_name,
    restoreSeats: wire.restore_seats,
    restoreAmount: wire.restore_amount,
    discountsRestartAtRestore: wire.discounts_restart_at_restore,
  };
}

/**
 * Validates the retention offer descriptor leadbilling sends in `data.offer`
 * of the retention-flow session (the offer resolved for the chosen reason, or
 * the offer applied on an accept).
 *
 * The cancellation flow renders ONLY what this returns, so it refuses what it
 * cannot render honestly instead of guessing:
 *  - `null` for a missing descriptor, an offer type this screen does not know,
 *    or an offer without the terms it needs. The caller treats that as a failed
 *    lookup, never as "no offer": an offer the customer never saw must not
 *    silently turn into a scheduled cancellation.
 *  - `offer: null` when the server answered that there is no offer, with its
 *    reason.
 *  - a discount keeps its percent and months even when the amounts are absent
 *    (the server leaves both null when it cannot price exactly). A price with
 *    one amount, or an offer amount above the current one, is dropped whole
 *    rather than half-shown.
 */
export function adaptRetentionOffer(
  payload: unknown,
): tRetentionOfferAnswer | null {
  if (!payload || typeof payload !== 'object') return null;
  const wire = payload as Record<string, unknown>;
  const ineligibleReason = isString(wire.ineligible_reason)
    ? wire.ineligible_reason
    : null;

  if (wire.code === null) {
    return { offer: null, ineligibleReason };
  }
  if (!isString(wire.code) || wire.code === '') return null;

  const code = wire.code;

  switch (wire.type) {
    case 'DISCOUNT': {
      const percentOff = wire.percent_off;
      if (!isFiniteNumber(percentOff) || percentOff <= 0 || percentOff > 100) {
        return null;
      }
      if (!isPositiveInteger(wire.duration_months)) return null;

      const hasExactPrice =
        isString(wire.currency) &&
        wire.currency !== '' &&
        isMinorUnitAmount(wire.current_amount) &&
        isMinorUnitAmount(wire.offer_amount) &&
        wire.offer_amount <= wire.current_amount;

      return {
        offer: {
          type: 'DISCOUNT',
          code,
          percentOff,
          durationMonths: wire.duration_months,
          pricing: hasExactPrice
            ? {
                currency: (wire.currency as string).toUpperCase(),
                currentAmount: wire.current_amount as number,
                offerAmount: wire.offer_amount as number,
              }
            : null,
        },
        ineligibleReason,
      };
    }
    case 'EXTENSION':
      if (!isPositiveInteger(wire.duration_days)) return null;
      return {
        offer: { type: 'EXTENSION', code, durationDays: wire.duration_days },
        ineligibleReason,
      };
    case 'DOWNGRADE':
      return { offer: { type: 'DOWNGRADE', code }, ineligibleReason };
    case 'OTHER': {
      const pause = adaptPlanPauseTerms(wire.pause);
      return pause
        ? {
            offer: { type: 'PLAN_PAUSE', code, pause },
            ineligibleReason,
          }
        : null;
    }
    default:
      return null;
  }
}

/**
 * The refusal code of a rejected billing call, or null when there is none.
 *
 * Reads ONLY the top-level `code`: bff-web relays leadbilling's refusals as
 * `{code, message}`, and until that relay deploys it forwards leadbilling's 422
 * body verbatim, which already carries a top-level `code`. Nothing else in a
 * refusal is dependable, and `message` is fallback copy, never UI text. A
 * numeric `code` is the bff envelope's HTTP status, not a refusal code.
 */
export function readBillingRefusalCode(rejection: unknown): string | null {
  if (!rejection || typeof rejection !== 'object') return null;
  const { code } = rejection as Record<string, unknown>;
  return isString(code) && code.trim() !== '' ? code : null;
}

/**
 * A refusal billing gave a reason for when asked to make a card the default
 * (RET-2347, RET-2155). Same idea as `WalletDeclinedError`: the reason travels
 * typed, so the caller can say what happened instead of "something went wrong".
 *
 * Only `makePaymentMethodDefault` rejects with it, and only for a body that
 * names a status this app knows. Every other failure still rejects with the
 * string it always did, so a caller that does not look for this class behaves
 * exactly as before.
 */
export class PaymentMethodDefaultRefusedError extends Error {
  readonly refusal: tPaymentMethodDefaultRefusal;

  constructor(refusal: tPaymentMethodDefaultRefusal) {
    super(`payment method default refused: ${refusal.status}`);
    this.name = 'PaymentMethodDefaultRefusedError';
    this.refusal = refusal;
  }
}

function readNonEmptyString(value: unknown): string | null {
  return isString(value) && value.trim() !== '' ? value : null;
}

/**
 * The refusal in the body of a failed "make default" call, or null when the
 * body names none this app knows.
 *
 * leadbilling answers `{success: false, data: [], message, errors}` and bff-web
 * forwards the body of a 400 or 422 verbatim. `errors` is an OBJECT carrying
 * `status` for the refusals that have a reason, and an ARRAY (empty, or of
 * strings such as `exceptionOcurred`) for the older answers: only the object
 * is read. `message` is the backend's English fallback, never UI text.
 */
export function readPaymentMethodDefaultRefusal(
  body: unknown,
): tPaymentMethodDefaultRefusal | null {
  if (!body || typeof body !== 'object') return null;
  const { errors } = body as Record<string, unknown>;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return null;
  }
  const wire = errors as Record<string, unknown>;

  switch (wire.status) {
    case 'paymentMethodBrandNotSupported':
      return {
        status: 'paymentMethodBrandNotSupported',
        brand: readNonEmptyString(wire.brand),
        currency: readNonEmptyString(wire.currency),
      };
    case 'paymentMethodDeclined':
      return {
        status: 'paymentMethodDeclined',
        declineCode: readNonEmptyString(wire.declineCode),
        // Strictly `true`: billing sends `false` when a subscription kept the
        // refused card, and a body without the field confirms nothing either.
        previousCardRestored: wire.previousCardRestored === true,
      };
    default:
      return null;
  }
}

/**
 * What a successful "make default" call did with the card, read from its 200
 * body (RET-2428).
 *
 * leadbilling answers `{success: true, data: {madeDefault: false}, ...}` when
 * the workspace is in a trial the customer has not confirmed: the card was
 * saved and NOT made the default. Every other success keeps its old body,
 * `data: []`, and bff-web forwards the body verbatim.
 *
 * A boolean `madeDefault` in an OBJECT `data` is authoritative: `false` reads
 * as `savedWithoutDefault`, `true` as `madeDefault`. Anything else (a body
 * without the field, `data: []`, `null`, a string, a non-boolean
 * `madeDefault`) reads as `unreported`: the caller decides, because only it
 * knows whether the workspace is in a trial
 * (`resolvePaymentMethodDefaultOutcome`).
 */
export function readPaymentMethodDefaultOutcome(
  body: unknown,
): tPaymentMethodDefaultReport {
  if (!body || typeof body !== 'object') return 'unreported';
  const { data } = body as Record<string, unknown>;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return 'unreported';
  }

  const { madeDefault } = data as Record<string, unknown>;
  if (madeDefault === false) return 'savedWithoutDefault';
  if (madeDefault === true) return 'madeDefault';

  return 'unreported';
}

export const SubscriptionManagerService = {
  /**
   * This function is used attach a payment method to a customer
   */
  attachPaymentMethodToCustomer: async function (
    workspaceId: string,
  ): Promise<tAttachPaymentMethodResponse> {
    const config = { isBFFService: true, headers: {} };

    try {
      const response = await AxiosService.post<
        void,
        tAttachPaymentMethodResponse
      >(`/workspace/${workspaceId}/payment-methods/attach`, undefined, config);
      return response;
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to add seats to a workspace
   */
  addSeats: async function (
    workspaceId: string,
    seats: number,
  ): Promise<tAddedSeats> {
    const url = `/workspace/${workspaceId}/seats/add`;
    const config = { isBFFService: true, headers: {} };
    const payload = {
      quantity: seats,
    };
    try {
      const addedSeatsResponse = await AxiosService.post<
        tSeatsResponse,
        tLeadsalesResponse<tAddedSeats>
      >(url, payload, config);
      return Promise.resolve(addedSeatsResponse.data as tAddedSeats);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to add seats to a workspace
   */
  removeSeats: async function (
    workspaceId: string,
    seats: number,
  ): Promise<tAddedSeats> {
    const url = `/workspace/${workspaceId}/seats/remove`;
    const config = { isBFFService: true };
    const payload = {
      quantity: seats,
    };
    try {
      const addedSeatsResponse = await AxiosService.post<
        tSeatsResponse,
        tLeadsalesResponse<tAddedSeats>
      >(url, payload, config);
      return Promise.resolve(addedSeatsResponse.data as tAddedSeats);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to purchase addons for a workspace
   */
  purchaseAddOn: async function (
    workspaceId: string,
    addons: tAddOnToPurchase[],
  ): Promise<tAddedSeats> {
    const url = `/workspace/${workspaceId}/addons/purchase`;
    const config = { isBFFService: true };
    const payload = {
      addons,
    };
    try {
      const purchaseAddOnResponse = await AxiosService.post<
        tPurchaseAddOnPayload,
        tLeadsalesResponse<tAddedSeats>
      >(url, payload, config);
      return Promise.resolve(purchaseAddOnResponse.data);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to get available products for a workspace
   */
  getProducts: async function (workspaceId: string): Promise<tProducts> {
    const url = `/workspace/${workspaceId}/tiers/products`;
    try {
      const productsResponse =
        await AxiosService.get<tLeadsalesResponse<tProducts>>(url);
      return Promise.resolve(productsResponse.data);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * RET-1487 Sub-3 — fetch the workspace's feature-override rows so the
   * recurring-credits cancel can resolve the `workspace_override_id`. Mirrors
   * `getProducts` (same `/tiers/*` bff gateway). Returns the relayed rows; the
   * caller filters by `feature_id` + `enabled`.
   */
  getFeatureOverrides: async function (
    workspaceId: string,
  ): Promise<tWorkspaceFeatureOverride[]> {
    const url = `/workspace/${workspaceId}/tiers/overrides`;
    try {
      const response =
        await AxiosService.get<tLeadsalesResponse<tWorkspaceFeatureOverride[]>>(
          url,
        );
      return Promise.resolve(response.data ?? []);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to purchase products for a workspace
   */
  purchaseProduct: async function (
    workspaceId: string,
    options: tPurchaseProductOptions,
  ): Promise<tPurchaseProductResponse> {
    const { products, confirmStripe = true, purchaseIntentId } = options;
    const url = `/workspace/${workspaceId}/tiers/products`;
    const config = { isBFFService: true, timeout: STRIPE_TIMEOUT_MS };
    const payload: tPurchaseProductPayload = {
      products,
      confirmStripe,
      // Only include the idempotency intent when present so intent-less
      // callers keep their current behavior (no empty-string uuid sent).
      ...(purchaseIntentId ? { purchase_intent_id: purchaseIntentId } : {}),
    };
    try {
      const response = await AxiosService.post<
        tPurchaseProductPayload,
        tPurchaseProductResponse
      >(url, payload, config);

      return response;
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to cancel products for a workspace
   */
  cancelProduct: async function (
    workspaceId: string,
    products: tProductToPurchase[],
  ): Promise<void> {
    const url = `/workspace/${workspaceId}/tiers/products/cancel`;
    const config = { isBFFService: true, headers: {} };
    const payload = {
      products,
    };
    try {
      await AxiosService.post<
        { products: tProductToPurchase[] },
        tLeadsalesResponse<void>
      >(url, payload, config);
      return Promise.resolve();
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to get all Payment Methods for a given Stripe Customer ID
   */

  getPaymentMethods: async function (wid: string): Promise<{
    paymentMethods: tPaymentMethod[];
    defaultPaymentMethodId?: string;
    planPaymentMethodId?: string;
    trialChargePaymentMethodId: string | null;
  }> {
    try {
      const config = { isBFFService: true, headers: {} };
      const paymentMethodsResponse =
        await AxiosService.get<tGetPaymentsResponse>(
          `/workspace/${wid}/payment-methods`,
          config,
        );
      return Promise.resolve({
        // RET-2428: `createdAt` absent (older billing) reads as null, so the
        // trial confirmation shows no «Agregada el …» line for that card.
        paymentMethods: paymentMethodsResponse.data.paymentMethods.map(
          (paymentMethod) => ({
            ...paymentMethod,
            createdAt: paymentMethod.createdAt ?? null,
          }),
        ),
        // RET-2428: the card that paid the trial, while the trial awaits
        // confirmation. Null or absent (older billing): no preselection.
        trialChargePaymentMethodId:
          paymentMethodsResponse.data?.trialChargePaymentMethodId ?? null,
        defaultPaymentMethodId:
          paymentMethodsResponse.data?.defaultPaymentMethodId,
        // RET-2027: null (nothing charged to a card on its own) and absent
        // (older billing) both mean "no plan card" to the page.
        planPaymentMethodId:
          paymentMethodsResponse.data?.planPaymentMethodId ?? undefined,
      });
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(
        `Error while getting ${wid} payment methods.${error}`,
      );
    }
  },

  /**
   * This function is used to make an specific payment method the default for a given customer
   * @param paymentMethodId
   * @param wid
   */

  makePaymentMethodDefault: async function (
    paymentMethodId: string,
    wid: string,
  ): Promise<tPaymentMethodDefaultReport> {
    const payload = {
      workspace_id: wid,
    };

    try {
      const config = { isBFFService: true, headers: {} };

      const body = await AxiosService.put<
        tMakePaymentMethodDefaultPayload,
        unknown
      >(
        `/workspace/${wid}/payment-methods/${paymentMethodId}/default`,
        payload,
        config,
      );

      // RET-2428: the body now says whether the card became the default. A
      // trial the customer has not confirmed keeps it saved without it.
      return readPaymentMethodDefaultOutcome(body);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      // RET-2347: this used to reject with the template string below whatever
      // billing had answered, so its reason was lost on the way and both
      // callers could only say "it failed". A refusal that names its reason
      // now reaches them typed; anything else keeps the string.
      const refusal = readPaymentMethodDefaultRefusal(
        (error as AxiosError<unknown>)?.response?.data,
      );
      if (refusal) {
        return Promise.reject(new PaymentMethodDefaultRefusedError(refusal));
      }
      return Promise.reject(
        `Error while defaulting the payment method ${paymentMethodId} to the workspace ${wid}. Error: ${error}`,
      );
    }
  },

  /**
   * This function is used to pay any pending debt
   * Returns payment information including 3D Secure details if required
   */
  payPendingDebt: async function (
    workspaceId: string,
    paymentMethodId: string,
  ): Promise<tPayPendingDebtsResponse> {
    const config = { isBFFService: true, timeout: STRIPE_TIMEOUT_MS };
    const url = `/workspace/${workspaceId}/pay-pending-debts`;
    const payload = {
      payment_method_id: paymentMethodId,
    };
    try {
      const payPendingDebtResponse = await AxiosService.post<
        tPayPendingDebtsPayload,
        tPayPendingDebtsResponse
      >(url, payload, config);

      return Promise.resolve(payPendingDebtResponse);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * RET-2449: pays the pending debt asking for the response of contract 2
   * (`data.result`). `payPendingDebt` keeps the response of today.
   */
  payPendingDebtV2: async function (
    workspaceId: string,
    request: tPayPendingDebtsRequest,
  ): Promise<tPayPendingDebtsContract2Response> {
    const config = { isBFFService: true, timeout: STRIPE_TIMEOUT_MS };
    const url = `/workspace/${workspaceId}/pay-pending-debts`;
    const payload = { ...request, contract: 2 as const };
    try {
      const payPendingDebtResponse = await AxiosService.post<
        tPayPendingDebtsRequest & { contract: 2 },
        tPayPendingDebtsContract2Response
      >(url, payload, config);

      return Promise.resolve(payPendingDebtResponse);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to delete a payment method
   */
  deletePaymentMethod: async function (
    paymentMethodId: string,
    wid: string,
  ): Promise<void> {
    const config = {
      isBFFService: true,
      headers: {},
    };

    try {
      await AxiosService.deleteById<void>(
        `/workspace/${wid}/payment-methods`,
        paymentMethodId,
        config,
      );
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      throw new Error(error.response?.data.errors[0]);
    }
  },

  /**
   * This function is used to reactivate a subscription with scheduled cancellation
   */
  reactivateSubscription: async function (wid: string): Promise<void> {
    const config = { isBFFService: true };
    const url = `/workspace/${wid}/subscription/reactivate`;
    try {
      await AxiosService.post(url, {}, config);
      return Promise.resolve();
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to confirm a 3D Secure card payment
   */
  confirm3DSecurePayment: async function (
    wid: string,
    paymentIntentId: string,
    paymentIntentStatus: string,
  ): Promise<t3DSecureConfirmationResponse> {
    const config = { isBFFService: true, timeout: STRIPE_TIMEOUT_MS };
    const url = `/workspace/${wid}/tiers/confirm-3d-secure`;
    const payload = {
      payment_intent_id: paymentIntentId,
      payment_intent_status: paymentIntentStatus,
    };
    try {
      const response = await AxiosService.post<
        t3DSecureConfirmationPayload,
        t3DSecureConfirmationResponse
      >(url, payload, config);
      return response;
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service.confirm3DSecurePayment',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      throw error;
    }
  },
  /*
   * This function is used to get invoice preview for a workspace
   */
  getInvoicePreview: async function (
    workspaceId: string,
    request: tInvoicePreviewRequest,
    signal: AbortSignal,
  ): Promise<tInvoicePreview> {
    const url = `/workspace/${workspaceId}/billing/invoice-preview`;
    const config = {
      isBFFService: true,
      signal,
    };

    try {
      const invoicePreviewResponse = await AxiosService.post<
        tInvoicePreviewRequest,
        tInvoicePreview
      >(url, request, config);
      return Promise.resolve(invoicePreviewResponse);
    } catch (e) {
      // Don't log cancelled requests as errors
      if ((e as AxiosError).code === 'ERR_CANCELED') {
        return Promise.reject(e);
      }

      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data || error);
    }
  },

  /**
   * Reads the projected next renewal for the workspace with the chosen Lead
   * Agent tier added (RET-1287). Read-only: leadbilling never mutates Stripe
   * for this call. The amounts/lines/total come from the backend — the modal
   * never does client-side arithmetic. The wire shape is validated and adapted
   * here so the caller gets a fully-typed `tRenewalProjectionWireData` (no
   * `as unknown as` downstream); `next_payment_at` is omitted from the query
   * when the acceptance has not anchored a personal charge date yet.
   *
   * 404 (no billable subscription) and 422 (unsupported plan / invalid tier)
   * reject so the hook can fail-closed (confirm disabled + retry), not toast.
   */
  getRenewalProjection: async function (
    workspaceId: string,
    request: tRenewalProjectionRequest,
    signal?: AbortSignal,
  ): Promise<tRenewalProjectionWireData> {
    const url = `workspace/${workspaceId}/billing/renewal-projection`;
    const params: Record<string, string> = { tier: request.tier };
    if (request.nextPaymentAt) {
      params.nextPaymentAt = request.nextPaymentAt;
    }
    if (request.origin) {
      params.origin = request.origin;
    }
    const config = { isBFFService: true, params, signal };

    try {
      const response = await AxiosService.get<tRenewalProjectionEnvelope>(
        url,
        config,
      );
      const data = adaptRenewalProjection(response?.data);
      if (!data) {
        return Promise.reject(
          new Error('renewal-projection returned an unparseable payload'),
        );
      }
      return Promise.resolve(data);
    } catch (e) {
      // Cancelled fetches (tier switch abort) are expected — pass through
      // without logging so the hook can ignore them.
      if ((e as AxiosError).code === 'ERR_CANCELED') {
        return Promise.reject(e);
      }
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service.getRenewalProjection',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error in SubscriptionManager.service',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data || error);
    }
  },

  /**
   * This function is used to upgrade a subscription
   */
  upgradeSubscription: async function (
    workspaceId: string,
    request: tSubscriptionUpgradeRequest,
  ): Promise<tSubscriptionUpgradeResponse> {
    const url = `/workspace/${workspaceId}/subscription/update-subscription`;
    const config = { isBFFService: true, headers: {} };

    try {
      const upgradeResponse = await AxiosService.post<
        tSubscriptionUpgradeRequest,
        tSubscriptionUpgradeResponse
      >(url, request, config);
      return Promise.resolve(upgradeResponse);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error upgrading subscription',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  getLeadsByFunnel: async function (
    workspaceId: string,
  ): Promise<tLeadsByFunnel[]> {
    try {
      const response = await AxiosService.get<{
        data: { funnels: tLeadsByFunnel[] };
      }>(`/workspace/${workspaceId}/pipes/lead-count`);
      return Promise.resolve(response.data.funnels);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * This function is used to start a retention flow session
   */
  startRetentionFlow: async function (
    workspaceUuid: string,
  ): Promise<tRetentionFlowResponse> {
    const url = `/workspace/${workspaceUuid}/retention-flow/session`;
    const config = { isBFFService: true, headers: {} };
    const payload: tRetentionFlowStartPayload = {
      phase: 'start',
    };

    try {
      const response = await AxiosService.post<
        tRetentionFlowStartPayload,
        tRetentionFlowResponse
      >(url, payload, config);
      return Promise.resolve(response);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service.startRetentionFlow',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error starting retention flow',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to select a cancellation reason in the retention flow
   */
  selectRetentionReason: async function (
    workspaceUuid: string,
    payload: tRetentionFlowReasonSelectedPayload,
  ): Promise<tRetentionFlowResponse> {
    const url = `/workspace/${workspaceUuid}/retention-flow/session`;
    const config = { isBFFService: true, headers: {} };

    try {
      const response = await AxiosService.post<
        tRetentionFlowReasonSelectedPayload,
        tRetentionFlowResponse
      >(url, payload, config);
      return Promise.resolve(response);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service.selectRetentionReason',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error selecting retention reason',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to finalize the retention flow action (accept/reject offer)
   */
  finalizeRetentionAction: async function (
    workspaceUuid: string,
    payload: tRetentionFlowFinalActionPayload,
  ): Promise<tRetentionFlowResponse> {
    const url = `/workspace/${workspaceUuid}/retention-flow/session`;
    const config = { isBFFService: true, headers: {} };

    try {
      const response = await AxiosService.post<
        tRetentionFlowFinalActionPayload,
        tRetentionFlowResponse
      >(url, payload, config);
      return Promise.resolve(response);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service.finalizeRetentionAction',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error finalizing retention action',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  /**
   * This function is used to abort the retention flow session
   */
  abortRetentionFlow: async function (
    workspaceUuid: string,
  ): Promise<tRetentionFlowResponse> {
    const url = `/workspace/${workspaceUuid}/retention-flow/session`;
    const config = { isBFFService: true, headers: {} };
    const payload: tRetentionFlowAbortPayload = {
      phase: 'abort',
    };

    try {
      const response = await AxiosService.post<
        tRetentionFlowAbortPayload,
        tRetentionFlowResponse
      >(url, payload, config);
      return Promise.resolve(response);
    } catch (e) {
      const error = e as AxiosError<BillingError>;
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.service.abortRetentionFlow',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error aborting retention flow',
        error as unknown as Error,
      );
      return Promise.reject(error.response?.data);
    }
  },

  getPlanPauseResumePreview: async function (
    workspaceId: string,
  ): Promise<tPlanPauseResumePreview> {
    const response = await AxiosService.get<{
      success: boolean;
      data?: {
        amount_cents?: unknown;
        currency?: unknown;
        proration_date?: unknown;
      };
    }>(`/workspace/${workspaceId}/plan-pause/resume-preview`, {
      isBFFService: true,
      headers: {},
    });
    const data = response.data;
    if (
      response.success !== true ||
      typeof data?.amount_cents !== 'number' ||
      !Number.isSafeInteger(data.amount_cents) ||
      data.amount_cents <= 0 ||
      typeof data.currency !== 'string' ||
      data.currency === '' ||
      typeof data.proration_date !== 'number' ||
      !Number.isSafeInteger(data.proration_date)
    ) {
      throw new Error('Invalid plan-pause resume preview');
    }
    return {
      amountCents: data.amount_cents,
      currency: data.currency.toUpperCase(),
      prorationDate: data.proration_date,
    };
  },

  cancelPlanPause: async function (workspaceId: string): Promise<void> {
    const response = await AxiosService.post<
      Record<string, never>,
      { success: boolean }
    >(
      `/workspace/${workspaceId}/plan-pause/cancel`,
      {},
      { isBFFService: true, headers: {} },
    );
    if (response.success !== true) {
      throw new Error('Plan-pause cancellation was not confirmed');
    }
  },

  resumePlanPause: async function (
    workspaceId: string,
    prorationDate: number,
  ): Promise<tPlanPauseResumeResult> {
    const response = await AxiosService.post<
      { proration_date: number },
      {
        success: boolean;
        data?: {
          status?: unknown;
          invoice_id?: unknown;
          payment_intent_client_secret?: unknown;
          message?: unknown;
        };
      }
    >(
      `/workspace/${workspaceId}/plan-pause/resume`,
      { proration_date: prorationDate },
      { isBFFService: true, headers: {}, timeout: STRIPE_TIMEOUT_MS },
    );
    const data = response.data;
    const status = data?.status;
    if (
      response.success !== true ||
      !data ||
      (status !== 'ok' &&
        status !== 'pending' &&
        status !== 'requires_action' &&
        status !== 'failed')
    ) {
      throw new Error('Plan-pause resume was not confirmed');
    }
    return {
      status,
      invoiceId: typeof data.invoice_id === 'string' ? data.invoice_id : null,
      paymentIntentClientSecret:
        typeof data.payment_intent_client_secret === 'string'
          ? data.payment_intent_client_secret
          : null,
      message: typeof data.message === 'string' ? data.message : null,
    };
  },
};
